import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';

const AddFromBacklogDrawer = ({ isOpen, onClose, backlogItems = [], onAddItems }) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return backlogItems;
    return backlogItems.filter((i) => (i.title || '').toLowerCase().includes(q) || (i.description || '').toLowerCase().includes(q));
  }, [search, backlogItems]);

  const toggle = (id, checked) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const toggleAll = (checked) => {
    setSelectedIds(checked ? filtered.map((i) => i.id) : []);
  };

  const handleAdd = async () => {
    if (selectedIds.length === 0) return;
    await onAddItems(selectedIds);
    setSelectedIds([]);
    setSearch('');
    onClose();
  };

  if (!isOpen) return null;

  const overlay = (
    <div className="fixed inset-0 z-[100000] flex" data-test-id="add-from-backlog-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-xl bg-white h-full shadow-xl flex flex-col z-[100001]">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Add Items from Backlog</h3>
            <p className="text-sm text-gray-500">Select items to add to this sprint</p>
          </div>
          <button className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Close">
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="p-3 border-b">
          <div className="relative">
            <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search backlog items..." className="w-full rounded-md border border-gray-300 pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
        </div>

        <div className="p-3 border-b flex items-center gap-2 bg-gray-50">
          <input
            type="checkbox"
            className="rounded border-gray-300"
            checked={selectedIds.length > 0 && selectedIds.length === filtered.length}
            onChange={(e) => toggleAll(e.target.checked)}
          />
          <span className="text-sm text-gray-700">Select all ({filtered.length} items)</span>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {filtered.length === 0 ? (
            <div className="text-center text-gray-500 py-16">
              <div className="text-5xl mb-2">🔍</div>
              <div className="text-sm">No items found</div>
            </div>
          ) : (
            filtered.map((item) => (
              <label key={item.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-white hover:border-gray-300 cursor-pointer">
                <input type="checkbox" className="mt-1 rounded border-gray-300" checked={selectedIds.includes(item.id)} onChange={(e) => toggle(item.id, e.target.checked)} />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 text-sm">{item.title}</div>
                  {item.description && <div className="text-xs text-gray-600 line-clamp-2">{item.description}</div>}
                  <div className="mt-1 flex flex-wrap gap-2">
                    {item.domain && <span className="text-xs rounded-full border px-2 py-0.5 bg-gray-50">{item.domain}</span>}
                    {item.priority && <span className="text-xs rounded-full border px-2 py-0.5 bg-gray-50">{String(item.priority).toLowerCase()}</span>}
                  </div>
                </div>
              </label>
            ))
          )}
        </div>

        <div className="p-4 border-t bg-white flex items-center justify-between">
          <div className="text-sm text-gray-600">{filtered.length} backlog items</div>
          <div className="flex items-center gap-2">
            <button className="rounded-md border px-4 py-2 text-sm" onClick={onClose}>Cancel</button>
            <button className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50" disabled={selectedIds.length === 0} onClick={handleAdd}>
              Add {selectedIds.length} Item{selectedIds.length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render in a portal to escape any parent stacking contexts/overflow
  if (typeof document !== 'undefined' && document.body) {
    return ReactDOM.createPortal(overlay, document.body);
  }
  return overlay;
};

export default AddFromBacklogDrawer;


